<template>
  <div class="space-y-2 pb-16">
    <div class="flex items-center justify-between mb-2 pb-2 sticky top-0 bg-surface-base">
      <PlusIcon class="w-6 h-6 cursor-pointer" id="add-table-button" @click="addNewTable" />
      <ButtonGroup
        class="ml-3"
        id="status-filter"
        v-model="statusFilter"
        itemText="label"
        itemValue="value"
        variant="text"
        :options="statusFilterOptions"
        :sameWidthOptions="false"
      />
      <TextInputAutofocus
        v-model="searchQuery"
        placeholder="Search"
        inputClasses="h-8"
        classes="w-1/3"
      />
    </div>
    <PerformanceTablePodItem
      v-for="table in tablesToDisplay"
      :key="table.id"
      :allowToDelete="table.allowToDelete"
      :isDeleted="tableIdsToBeDeleted.includes(table.id)"
      :hasUpdates="table.hasUpdates"
      :shape="table.shape"
      :updateStatus="table.updateStatus"
      :reservationStatus="table.activeReservation ? table.activeReservation.status : null"
      :table="table"
      @click="goToPerformanceTableDetails(table.id)"
      @click:deleteTable="performanceUpdateStore.handleTableDeletion"
      @click:resetTable="performanceUpdateStore.resetTable"
    />
  </div>
</template>

<script lang="ts" setup>
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import PlusIcon from '@/assets/plus.svg?component'
import ButtonGroup from '@/common/components/molecules/ButtonGroup.vue'
import TextInputAutofocus from '@/common/components/molecules/TextInputAutofocus.vue'
import PerformanceTablePodItem from '@/modules/performance/components/molecules/PerformanceTablePodItem.vue'
import { PERFORMANCE_ROUTE_NAMES } from '@/modules/performance/constants/PerformanceRouteConstants'
import { TABLE_UPDATE_STATUS } from '@/modules/performance/interfaces/PerformanceTableInterfaces'
import { PerformanceTableWithReservations } from '@/modules/performance/stores/PerformanceReservationStore'
import { usePerformanceUpdateStore } from '@/modules/performance/stores/PerformanceUpdateStore'
import { cleanString } from '@/utils/utils'

import { PerformanceTableId } from '@generated/types'

type FilterOption = (typeof FILTER_OPTIONS)[keyof typeof FILTER_OPTIONS]
const FILTER_OPTIONS = { ALL: 'all', MODIFIED: 'modified' }
const statusFilterOptions = [
  { label: 'All', value: FILTER_OPTIONS.ALL },
  { label: 'Modified', value: FILTER_OPTIONS.MODIFIED },
]
const statusFilter = ref<FilterOption>(FILTER_OPTIONS.ALL)

const router = useRouter()
const performanceUpdateStore = usePerformanceUpdateStore()
const { tableIdsToBeDeleted, tablesWithUpdates, isSaving } = storeToRefs(performanceUpdateStore)

const searchQuery = ref<string>('')
const tablesBySearchQuery = computed<PerformanceTableWithReservations[]>(function () {
  if (_.isEmpty(searchQuery.value)) return tablesWithUpdates.value
  return _.filter(tablesWithUpdates.value, function (table) {
    return _.includes(cleanString(table.name), cleanString(searchQuery.value))
  })
})
const tablesToDisplay = computed<PerformanceTableWithReservations[]>(function () {
  const tablesByStatus = _.filter(tablesBySearchQuery.value, function (table) {
    if (statusFilter.value === FILTER_OPTIONS.ALL) return true
    return (
      table.updateStatus === TABLE_UPDATE_STATUS.NEW ||
      table.updateStatus === TABLE_UPDATE_STATUS.UPDATED ||
      _.includes(tableIdsToBeDeleted.value, table.id)
    )
  })
  return _.sortBy(tablesByStatus, 'name')
})

// Reset filters when saving is done, otherwise it will not show any tables
watch(isSaving, function (value, oldValue) {
  if (oldValue && !value) {
    searchQuery.value = ''
    statusFilter.value = FILTER_OPTIONS.ALL
  }
})

async function addNewTable() {
  const newTable = performanceUpdateStore.createTable()
  await router.replace({
    name: PERFORMANCE_ROUTE_NAMES.TABLE_EDIT,
    params: { tableId: newTable!.id },
  })
}
async function goToPerformanceTableDetails(tableId: PerformanceTableId) {
  await router.replace({
    name: PERFORMANCE_ROUTE_NAMES.TABLE_EDIT,
    params: { tableId },
  })
}
</script>
